/* ============================================================
   qua.domains — Responsive scaling engine  (V5 — base-density variant)
   ------------------------------------------------------------
   IDENTICAL to responsive.js EXCEPT desktop now renders at a global
   BASE_ZOOM (0.9) instead of 1. This reproduces, 1:1, what the design
   looks like at Chrome 90% zoom — using the codebase's OWN native scaling
   primitive (CSS `zoom`), which every JS art layer (circuit cable, hero
   field) already reads & compensates for via `parseFloat(page.style.zoom)`.

   Source design tokens stay canonical (a heading declared 100px is still
   "100px" in the design system). BASE_ZOOM is the single documented
   density knob — change one number to retune.

   ≥ 1030px  DESKTOP  · native layout, content locked at 1030, zoom = BASE_ZOOM
   768–1030  TABLET   · desktop design shrunk to fit; zoom capped at BASE_ZOOM
                        at the top edge so there's NO jump crossing 1030.
   < 768px   MOBILE   · dedicated 370px design, scaled with zoom (unchanged).
   ============================================================ */
(function () {
  'use strict';

  var DESKTOP   = 1030;
  var MOBILE_BP = 768;
  var MBASE     = 370;
  var MMAX      = 430 / MBASE;   // ~1.162 cap

  /* --- the one density knob. 1 = native, 0.9 = "Chrome 90% zoom" feel --- */
  var BASE_ZOOM = 0.9;

  var page, stage, nav;
  var mode = '';

  // running inside the Visual Builder canvas? (then skip the live hamburger so
  // it doesn't clutter the Layers tree / get saved into the page)
  var inBuilder = false;
  try { inBuilder = !!(window.frameElement && window.parent && window.parent !== window && window.parent.BLDR); } catch (e) {}

  function vw() {
    if (window.__VP_FORCE === 'mobile')  return 390;
    if (window.__VP_FORCE === 'tablet')  return 880;
    if (window.__VP_FORCE === 'desktop') return 1440;
    return document.documentElement.clientWidth;
  }

  function init() {
    page = document.querySelector('.page');
    if (!page) return;

    stage = document.getElementById('vp-mstage');
    if (!stage) {
      stage = document.createElement('div');
      stage.id = 'vp-mstage';
      page.parentNode.insertBefore(stage, page);
      stage.appendChild(page);
    }

    buildMobileMenu();
    compileMobileRules();
    apply();
    window.addEventListener('resize', apply, { passive: true });
    window.addEventListener('orientationchange', function () { setTimeout(apply, 60); });
    window.addEventListener('load', publishNavHeight);
  }

  /* --- inject the mobile hamburger + dropdown (survives Builder swaps) --- */
  function buildMobileMenu() {
    nav = document.querySelector('.nav');
    if (!nav || nav.querySelector('.m-burger')) return;
    var inner = nav.querySelector('.nav__inner') || nav;

    var burger = document.createElement('button');
    burger.className = 'm-burger';
    burger.setAttribute('aria-label', 'Menu');
    burger.setAttribute('aria-expanded', 'false');
    burger.innerHTML = '<span></span><span></span><span></span>';

    var panel = document.createElement('nav');
    panel.className = 'm-menu';
    panel.setAttribute('aria-label', 'Mobile');
    var links = document.querySelector('.nav__links');
    panel.innerHTML = links ? links.innerHTML
      : '<a href="#">Home</a><a href="#about">About</a><a href="#services">Catalog</a><a href="#resources">Resources</a><a href="#">Contact</a>';

    burger.addEventListener('click', function () {
      var open = nav.classList.toggle('m-open');
      burger.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
    panel.addEventListener('click', function (e) {
      if (e.target.closest('a')) { nav.classList.remove('m-open'); burger.setAttribute('aria-expanded', 'false'); }
    });

    // mark as runtime chrome: stripped on Builder save, ignored by selection
    burger.setAttribute('data-vp-skip', '1');
    panel.setAttribute('data-vp-skip', '1');

    var actions = nav.querySelector('.nav__actions') || inner;
    actions.appendChild(burger);
    nav.appendChild(panel);
  }

  /* --- compile per-element mobile overrides ---------------------------------- */
  function withImportant(decls) {
    return decls.split(';').map(function (d) {
      d = d.trim(); if (!d) return '';
      if (/!important\s*$/.test(d)) return d;
      return d + ' !important';
    }).filter(Boolean).join('; ');
  }
  function compileMobileRules() {
    if (!page) return;
    var sheet = document.getElementById('vp-mrules');
    if (!sheet) { sheet = document.createElement('style'); sheet.id = 'vp-mrules'; document.head.appendChild(sheet); }
    var out = [];
    page.querySelectorAll('[data-bldr-mcss]').forEach(function (el) {
      var decls = el.getAttribute('data-bldr-mcss'); if (!decls) return;
      var bid = el.getAttribute('data-bid');
      if (!bid) { bid = 'm' + (++compileMobileRules._n); el.setAttribute('data-bid', bid); }
      out.push('html[data-vp="mobile"] [data-bid="' + bid + '"]{' + withImportant(decls) + '}');
    });
    sheet.textContent = out.join('\n');
  }
  compileMobileRules._n = 0;
  window.__vpCompileMobile = compileMobileRules;
  window.__vpEnsureMenu = buildMobileMenu;

  function publishNavHeight() {
    if (!nav || !page) return;
    // nav height in the page's own CSS px (zoom-independent) → sticky offset
    var h = nav.getBoundingClientRect().height;
    var z = parseFloat(page.style.zoom) || 1;
    page.style.setProperty('--mnav-h', Math.round(h / z) + 'px');
  }

  function apply() {
    if (!page) return;
    var w = vw();
    var root = document.documentElement;

    if (w >= DESKTOP) {
      // desktop now renders at the global base density (was: zoom = '')
      if (mode !== 'desktop') {
        mode = 'desktop';
        root.setAttribute('data-vp', 'desktop');
        page.style.width = '';
        page.style.margin = '';
      }
      page.style.zoom = BASE_ZOOM;
    } else if (w >= MOBILE_BP) {
      mode = 'tablet';
      root.setAttribute('data-vp', 'tablet');
      page.style.width = '';
      page.style.margin = '';
      // cap at BASE_ZOOM so there's no size jump when crossing the 1030 line
      page.style.zoom = Math.min(BASE_ZOOM, w / DESKTOP);
    } else {
      mode = 'mobile';
      root.setAttribute('data-vp', 'mobile');
      page.style.width = MBASE + 'px';
      page.style.margin = '0 auto';
      page.style.zoom = Math.min(w / MBASE, MMAX);
    }
    publishNavHeight();
  }

  window.__vpResize = apply;

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
